import { memo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../../common/components/xepp/button/Button";
import styles from "./CreateDepartmentForm.module.scss";
import { DepartmentsActiveIcon } from "../../../../assets/sidemenu-icons";
import { useGetUtilityUsersQuery, useCreateDepartmentMutation } from "../../../../service/department-service";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { toast } from "react-toastify";

type Props = {
	onCancel: () => void;
};

const CreateDepartmentForm = memo((props: Props) => {
	const { onCancel } = props;
	const navigate = useNavigate();

	const { data: utilityUsers, isLoading, isError } = useGetUtilityUsersQuery();
	const [createDepartment, { isLoading: isCreating }] = useCreateDepartmentMutation();

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		utilityStaff: [] as string[],
		supervisor: "",
	});

	const [isStaffDropdownOpen, setIsStaffDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsStaffDropdownOpen(false);
			}
		};

		if (isStaffDropdownOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isStaffDropdownOpen]);

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleUtilityStaffToggle = (userId: string) => {
		setFormData((prev) => ({
			...prev,
			utilityStaff: prev.utilityStaff.includes(userId)
				? prev.utilityStaff.filter((id) => id !== userId)
				: [...prev.utilityStaff, userId],
		}));
	};

	const getSelectedStaffText = () => {
		if (formData.utilityStaff.length === 0) return "Select utility staff";
		if (formData.utilityStaff.length === 1) {
			const user = utilityUsers?.find((u) => u.id === formData.utilityStaff[0]);
			return user ? user.name : "1 selected";
		}
		return `${formData.utilityStaff.length} staff members selected`;
	};

	const isFormValid = () => {
		return (
			formData.name.trim() !== "" &&
			formData.description.trim() !== "" &&
			formData.utilityStaff.length > 0 &&
			formData.supervisor !== ""
		);
	};

	const handleSubmit = async () => {
		if (!isFormValid()) return;

		try {
			await createDepartment({
				name: formData.name,
				description: formData.description,
				supervisorId: formData.supervisor,
				departmentMembers: formData.utilityStaff,
			}).unwrap();

			toast.success("Department created successfully!");
			navigate(-1); // Go back to the departments list
		} catch (error: any) {
			toast.error(error?.data?.message || "Failed to create department. Please try again.");
		}
	};

	if (isError) {
		return (
			<div className={styles.container}>
				<div className={styles.errorContainer}>
					<p>Failed to load utility users. Please try again.</p>
					<Button text="Go Back" variant="secondary" onClick={onCancel} />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<CommonLoadingSpinner loading={isLoading}>
				<div className={styles.formContainer}>
					<div className={styles.formHeader}>
						<DepartmentsActiveIcon height={40} width={40} />
						<div className={styles.formTitle}>Create Department</div>
					</div>

					<div className={styles.formContent}>
						<div className={styles.formGroup}>
							<label className={styles.label}>
								Name <span className={styles.required}>*</span>
							</label>
							<input
								type="text"
								className={styles.input}
								placeholder="Enter Department Name"
								value={formData.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
							/>
						</div>

						<div className={styles.formGroup}>
							<label className={styles.label}>
								Description <span className={styles.required}>*</span>
							</label>
							<textarea
								className={styles.textarea}
								placeholder="Please provide a brief description of action to be taken in this step."
								value={formData.description}
								onChange={(e) => handleInputChange("description", e.target.value)}
								rows={5}
							/>
						</div>

						<div className={styles.formGroup}>
							<label className={styles.label}>
								Add Utility Staff <span className={styles.required}>*</span>
							</label>
							<div className={styles.customDropdown} ref={dropdownRef}>
								<button
									type="button"
									className={styles.dropdownToggle}
									onClick={() => setIsStaffDropdownOpen(!isStaffDropdownOpen)}
								>
									<span>{getSelectedStaffText()}</span>
									<span className={styles.dropdownArrow}>▼</span>
								</button>
								{isStaffDropdownOpen && (
									<div className={styles.dropdownMenu}>
										{utilityUsers?.map((user) => (
											<label key={user.id} className={styles.checkboxItem}>
												<input
													type="checkbox"
													checked={formData.utilityStaff.includes(user.id)}
													onChange={() => handleUtilityStaffToggle(user.id)}
													className={styles.checkbox}
												/>
												<span className={styles.checkboxLabel}>
													{user.name}
												</span>
											</label>
										))}
									</div>
								)}
							</div>
						</div>

						<div className={styles.formGroup}>
							<label className={styles.label}>
								Assign Supervisor <span className={styles.required}>*</span>
							</label>
							<select
								className={styles.select}
								value={formData.supervisor}
								onChange={(e) => handleInputChange("supervisor", e.target.value)}
							>
								<option value="">Select</option>
								{utilityUsers?.map((user) => (
									<option key={user.id} value={user.id}>
										{user.name}
									</option>
								))}
							</select>
						</div>
					</div>

					<div className={styles.formActions}>
						<Button text="Cancel Setup" variant="secondary" onClick={onCancel} disabled={isCreating} />
						<Button
							text={isCreating ? "Creating..." : "Create Department"}
							variant="primary"
							onClick={handleSubmit}
							disabled={!isFormValid() || isCreating}
						/>
					</div>
				</div>
			</CommonLoadingSpinner>
		</div>
	);
});

export default CreateDepartmentForm;
